import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import { FleetLandingHeader, FleetLandingDock } from './FleetLandingNavigation';
import LandingNavIcon from './LandingNavIcon';
import { propertyFetch } from '../services/idxPropertyClient';
import { submitChatLead } from '../services/chatLeadService';
// src/components/LandingPage.js
import React, { useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import ActionButtons from './ActionButtons';
import PropertyDetailModal from './PropertyDetailModal';
import usePropertyDetail from '../hooks/usePropertyDetail';
import BlogsPage from './BlogsPage';
import ReviewsPage from './ReviewsPage';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './LandingPage.css';
import './IntroVideo.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

import './FleetLandingNavigation.css';

const MARICOPA_RESOURCE_GROUPS = [
  {
    title: 'Buy in Maricopa',
    links: [
      ['Homes for Sale', '/guide/homes-for-sale-maricopa-az'],
      ['New Construction', '/guide/maricopa-az-new-construction-homes'],
      ['Golf Course Homes', '/guide/maricopa-az-golf-course-homes'],
      ['RV Garage Homes', '/guide/maricopa-az-rv-garage-homes']
    ]
  },
  {
    title: 'Moving Here',
    links: [
      ['Living in Maricopa', '/guide/living-in-maricopa-az'],
      ['Relocation Checklist', '/guide/maricopa-az-relocation-checklist'],
      ['SR 347 & Commuting', '/guide/maricopa-az-commute-sr-347'],
      ['Neighborhood Guide', '/guide/maricopa-az-neighborhoods-guide']
    ]
  },
  {
    title: 'Local Research',
    links: [
      ['HOA Directory', '/maricopa-hoas'],
      ['347 Business Directory', '/347-directory'],
      ['Add Your Business + Chamber App', '/347-directory/setup'],
      ['Maricopa First Fridays', '/first-fridays'],
      ['Housing Market', '/guide/maricopa-az-housing-market'],
      ['Schools Guide', '/guide/maricopa-az-schools'],
      ['Things to Do', '/guide/things-to-do-maricopa-az']
    ]
  },
  {
    title: 'Sell with Confidence',
    links: [
      ['Sell a Maricopa Home', '/guide/sell-my-home-maricopa-az'],
      ['What Is My Home Worth?', '/guide/maricopa-az-home-values'],
      ['Meet Sandy Hopkins', '/agent'],
      ['Client Reviews', '/reviews']
    ]
  }
];

const LandingPage = ({ directListing = false, directSearch = false }) => {
  const { listingKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const landingPageConfig = SITE_CONFIG.LANDING_PAGE;
  const [activeSection, setActiveSection] = useState(null);
  const [showMapPage, setShowMapPage] = useState(false);
  const [showGlobeMap, setShowGlobeMap] = useState(directListing || directSearch || location.state?.openGlobeMap);
  const [, setShowGlobeMapResults] = useState(false);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignInDropdown, setShowSignInDropdown] = useState(false);
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
    name: ''
  });
  const [showIntroVideo, setShowIntroVideo] = useState(!directListing && !directSearch);
  const [showMLSDisclosure, setShowMLSDisclosure] = useState(false);
  const reviewCarouselRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const [showAgentSection, setShowAgentSection] = useState(false);
  const [showImagePageText, setShowImagePageText] = useState(false);
  const [showEPage, setShowEPage] = useState(false);
  const [showCityTiles, setShowCityTiles] = useState(false);
  const [featuredProperties, setFeaturedProperties] = useState([]);

  // Details and galleries load when a visitor opens a property.

  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const propertyTouchStartX = React.useRef(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false,
    serviceReq: 'buying'
  });
  const [submittingConnect, setSubmittingConnect] = useState(false);
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  const [showHomeValueModal, setShowHomeValueModal] = useState(false);
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false });
  const [submittingHomeValue, setSubmittingHomeValue] = useState(false);
  const [showHomeValueSuccess, setShowHomeValueSuccess] = useState(false);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { propertyDetail, closePropertyDetail, propertyDetailImages, imagesLoading, openPropertyFromSummary } = usePropertyDetail();

  const SERVICE_AREAS = [
    { label: 'Maricopa', city: 'Maricopa', state: 'AZ' },
    { label: 'Ahwatukee', city: 'Phoenix', state: 'AZ' },
    { label: 'Chandler', city: 'Chandler', state: 'AZ' },
    { label: 'Queen Creek', city: 'Queen Creek', state: 'AZ' },
    { label: 'Gilbert', city: 'Gilbert', state: 'AZ' },
    { label: 'Tempe', city: 'Tempe', state: 'AZ' },
    { label: 'Mesa', city: 'Mesa', state: 'AZ' },
    { label: 'Casa Grande', city: 'Casa Grande', state: 'AZ' },
    { label: 'Goodyear', city: 'Goodyear', state: 'AZ' },
    { label: 'Buckeye', city: 'Buckeye', state: 'AZ' },
    { label: 'San Tan Valley', city: 'San Tan Valley', state: 'AZ' },
    { label: 'Coolidge', city: 'Coolidge', state: 'AZ' },
  ];

  const [selectedCity, setSelectedCity] = useState(null);

  const handleAreaSearch = (event) => {
    const index = event.target.value;
    if (index === '') return;
    event.target.value = '';
    const area = SERVICE_AREAS[index];
    if (!area) return;
    sessionStorage.removeItem('globeMapData');
    const params = new URLSearchParams({ city: area.city, state: area.state, location: area.city, view: 'map' });
    navigate(`/search?${params}`);
  };

  React.useEffect(() => {
    const video = videoRef.current;
    if (!showIntroVideo || showGlobeMap || !video) return;
    const connection = navigator.connection || {};
    const shouldPause = connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    video.src = landingPageConfig.HERO_VIDEO;
    video.muted = true;
    video.load();
    if (shouldPause) return undefined;
    const tryPlay = () => video.play().catch(() => {});
    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('loadeddata', tryPlay, { once: true });
      return () => video.removeEventListener('loadeddata', tryPlay);
    }
  }, [landingPageConfig.HERO_VIDEO, showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    if (showIntroVideo && !showGlobeMap) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
  }, [showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    const handleReviewScroll = () => {
      if (!reviewCarouselRef.current) return;

      const container = reviewCarouselRef.current;
      const cards = container.querySelectorAll('.review-card');
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      cards.forEach(card => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const distance = Math.abs(containerCenter - cardCenter);
        const maxDistance = containerRect.height / 2;

        const opacity = Math.max(0.3, 1 - (distance / maxDistance) * 0.7);
        const scale = Math.max(0.9, 1 - (distance / maxDistance) * 0.1);

        card.style.opacity = opacity;
        card.style.transform = `scale(${scale})`;
      });
    };

    const carousel = reviewCarouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleReviewScroll);
      handleReviewScroll();
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener('scroll', handleReviewScroll);
      }
    };
  }, [showIntroVideo]);

  React.useEffect(() => {
    if (directSearch) {
      setShowIntroVideo(false);
      setSelectedCity(null);
      setShowGlobeMap(true);
    }
  }, [directSearch, location.key]);

  React.useEffect(() => {
    // Hide intro video when on a direct listing route
    if (directListing && listingKey) {
      setShowIntroVideo(false);
      setShowGlobeMap(true);
    }
  }, [directListing, listingKey]);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportHeight = window.innerHeight;

      if (showIntroVideo && !showGlobeMap) {
        if (scrollTop >= viewportHeight * 0.5 && !showAgentSection) {
          setShowAgentSection(true);
        } else if (scrollTop < viewportHeight * 0.5 && showAgentSection) {
          setShowAgentSection(false);
        }

        if (scrollTop >= viewportHeight * 2.5 && !showImagePageText) {
          setShowImagePageText(true);
        } else if (scrollTop < viewportHeight * 2.5 && showImagePageText) {
          setShowImagePageText(false);
        }

        if (scrollTop >= viewportHeight * 3.5 && !showEPage) {
          setShowEPage(true);
        } else if (scrollTop < viewportHeight * 3.5 && showEPage) {
          setShowEPage(false);
        }

        if (scrollTop >= viewportHeight * 4.5 && !showCityTiles) {
          setShowCityTiles(true);
        } else if (scrollTop < viewportHeight * 4.5 && showCityTiles) {
          setShowCityTiles(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showIntroVideo, showGlobeMap, showAgentSection, showImagePageText, showEPage, showCityTiles]);

  React.useEffect(() => {
    let raf = null;
    const update = () => {
      raf = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // Distance (px) from the moment the featured section is centered in the viewport.
      const imgParallax = scrollTop - window.innerHeight * 3;
      document.documentElement.style.setProperty('--img-parallax', String(imgParallax));
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Fetch featured properties
    const fetchFeaturedProperties = async () => {
      try {
        const [privateRes, brokerageRes, mlsRes] = await Promise.all([
          fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
            headers: { 'x-api-key': 'treas-api-key-2025' }
          }),
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?listOfficeKey=${encodeURIComponent(SITE_CONFIG.AGENT.BROKERAGE_LICENSE)}&minPrice=500000&maxPrice=600000&daysonmarketSort=newold&firstPhoto=true&hasMedia=true&pageSize=20&state=${SITE_CONFIG.STATE}`, {}),
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?minPrice=500000&maxPrice=600000&daysonmarketSort=newold&firstPhoto=true&hasMedia=true&pageSize=20&state=${SITE_CONFIG.STATE}`, {})
        ]);

        const privateListings = privateRes.ok ? (await privateRes.json()).listings || [] : [];
        const brokerageProperties = brokerageRes.ok ? (await brokerageRes.json()).properties || [] : [];
        const mlsProperties = mlsRes.ok ? (await mlsRes.json()).properties || [] : [];

        const mappedPrivate = await Promise.all(privateListings.map(async l => {
          let first_photo_url = l.main_image_url || null;
          try {
            const mediaRes = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${l.id}`, {
              headers: { 'x-api-key': 'treas-api-key-2025' }
            });
            if (mediaRes.ok) {
              const mediaData = await mediaRes.json();
              const sorted = (mediaData.media || []).sort((a, b) => a.order_hint - b.order_hint);
              if (sorted.length > 0) first_photo_url = sorted[0].media_url || sorted[0].s3_link;
            }
          } catch (e) {}
          return {
            ...l,
            listing_key: `PRIVATE_${l.id}`,
            current_price: l.listing_type === 'sale' ? l.price_sale : l.price_lease_monthly,
            street_number: l.address_line_1?.split(' ')[0] || '',
            street_name: l.address_line_1?.split(' ').slice(1).join(' ') || '',
            first_photo_url,
            isPrivate: true
          };
        }));

        const combined = [...mappedPrivate, ...brokerageProperties, ...mlsProperties]
          .filter((p, i, arr) => arr.findIndex(x => x.listing_key === p.listing_key) === i)
          .slice(0, 20);
        setFeaturedProperties(combined);
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      }
    };

    fetchFeaturedProperties();

    // Preload city tile images
    const cityTileImages = [
      'generic-az-scenerey-001.png', 'generic-az-scenerey-002.png', 'generic-az-scenerey-003.png',
      'generic-az-scenerey-004.png', 'generic-az-scenerey-005.png', 'generic-az-scenery-006.png',
      'generic--az-southmountain-001.jpg', 'generic-az-southmountain-002.png',
      'generic-az-south-street.jpeg', 'generic-southmountain-home-001.jpg'
    ];
    cityTileImages.forEach(src => { new Image().src = `https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/${src}`; });

    // Handle direct listing access
    if (directListing && listingKey) {
      // Store listing key for ActionButtons to use
      sessionStorage.setItem('directListingKey', listingKey);
    }

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch('https://tophamtechbeb.com/aw-monitor/v1/page-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            page_path: window.location.pathname,
            referrer: document.referrer || '',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            user_agent: navigator.userAgent,
            session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9)
          })
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Math.random().toString(36).substr(2, 9));
    }

    trackPageView();

    // Listen for globe map open event from AgentInfo
    const handleOpenGlobeMap = () => {
      setShowGlobeMap(true);
    };

    window.addEventListener('openGlobeMap', handleOpenGlobeMap);

    return () => {
      window.removeEventListener('openGlobeMap', handleOpenGlobeMap);
    };
  }, [directListing, listingKey]);

  const handleToggle = () => {
    console.log('Toggle clicked, current mode:', isDarkMode);
    toggleTheme();
  };

  const handleSignInToggle = () => {
    setShowSignInDropdown(!showSignInDropdown);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: signInUsername,
          password: signInPassword,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
        localStorage.setItem('user', JSON.stringify(userData));
        setShowSignInDropdown(false);
        setShowForgotPassword(false);
        setSignInUsername('');
        setSignInPassword('');
        setResetEmail('');
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...registerData,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        // Auto sign in after registration
        const signInResponse = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: registerData.username,
            password: registerData.password,
            siteId: SITE_CONFIG.SITE_ID
          })
        });

        if (signInResponse.ok) {
          const userData = await signInResponse.json();
          setUser(userData);
          setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
          localStorage.setItem('user', JSON.stringify(userData));
          setShowSignInDropdown(false);
          setRegisterData({ username: '', email: '', password: '', phoneNumber: '', name: '' });
        }
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogOut = () => {
    setUser(null);
    setIsAdmin(false);
    setShowSignInDropdown(false);
    localStorage.removeItem('user');
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          baseUrl: window.location.origin,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        alert('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to send reset link. Please try again.');
    }
  };

  const handleHomeValueSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowHomeValueModal(false);
      setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
      setShowHomeValueSuccess(true);
      setTimeout(() => setShowHomeValueSuccess(false), 6000);
      return;
    }

    setSubmittingHomeValue(true);
    try {
      const params = new URLSearchParams({
        postalCode: homeValueForm.postalCode,
        name: homeValueForm.name,
        phoneNumber: homeValueForm.phoneNumber,
        smsConsent: homeValueForm.smsConsent ? 'true' : 'false',
        serviceReq: 'estimate',
        email: homeValueForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });
      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });
      if (response.ok) {
        setShowHomeValueModal(false);
        setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
        setShowHomeValueSuccess(true);
        setTimeout(() => setShowHomeValueSuccess(false), 6000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Home value form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingHomeValue(false);
    }
  };

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowConnectModal(false);
      setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
      setShowConnectSuccess(true);
      setTimeout(() => setShowConnectSuccess(false), 5000);
      return;
    }

    setSubmittingConnect(true);
    try {
      const params = new URLSearchParams({
        postalCode: connectForm.postalCode,
        name: connectForm.name,
        phoneNumber: connectForm.phoneNumber,
        smsConsent: connectForm.smsConsent ? 'true' : 'false',
        serviceReq: connectForm.serviceReq,
        email: connectForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });

      if (response.ok) {
        setShowConnectModal(false);
        setShowConnectSuccess(true);
        setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Connect form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingConnect(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hidden content for SEO crawlers */}
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}}>
        <h1>Real Estate Services - Buy, Sell, Lease Properties</h1>
        <p>Professional real estate agent Sandy Hopkins provides expert home buying, selling, and leasing services. Chat with our AI assistant for instant property search and real estate advice.</p>
        <h2>Services We Offer:</h2>
        <ul>
          <li>Home Buying Assistance - Find your dream home with expert guidance</li>
          <li>Property Selling Services - Get the best price for your home</li>
          <li>Rental and Leasing - Find the perfect rental property</li>
          <li>Real Estate Consultation - Professional advice for all your property needs</li>
          <li>Property Search - Advanced search tools and personalized recommendations</li>
        </ul>
        <h2>Why Choose Sandy Hopkins?</h2>
        <p>Local expertise, personalized service, and cutting-edge technology including AI-powered chat assistance for instant help with your real estate questions.</p>
      </div>

      <FleetLandingDock hidden={showBlogs || showReviews || showGlobeMap || showMapPage} isDarkMode={isDarkMode} onSearch={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setSelectedCity(null);setShowGlobeMap(true);}} onChat={() => navigate('/chat')} onJournal={() => navigate('/blogs')} onReviews={() => navigate('/reviews')} onCloseAccount={() => setShowSignInDropdown(false)} isAdmin={isAdmin} onAdmin={() => { window.location.href = '/admin'; }}><div className="signin-dropdown"><button type="button" className="fleet-dock-button signin-toggle" onClick={handleSignInToggle} aria-expanded={showSignInDropdown} aria-haspopup="dialog"><LandingNavIcon name="account" /><span>{user ? 'Account' : 'Sign in'}</span></button>{showSignInDropdown && (
            <div className="signin-dropdown-content" role="dialog" aria-label={user ? 'Account menu' : 'Sign in'}>
              {!user ? (
                showForgotPassword ? (
                  resetSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <h3 style={{color: '#28a745', margin: '0 0 15px 0'}}>✓ Email Sent!</h3>
                      <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>Please check your email for password reset instructions.</p>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  ) : (
                    <div>
                      <form onSubmit={handlePasswordResetSubmit} className="signin-form">
                        <h3>Reset Password</h3>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <button type="submit">
                          Send Reset Link
                        </button>
                      </form>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  )
                ) : !showRegisterForm ? (
                  <div>
                    <form onSubmit={handleSignIn} className="signin-form">
                      <h3>Sign In</h3>
                      <input
                        type="text"
                        placeholder="Username or Email"
                        value={signInUsername}
                        onChange={(e) => setSignInUsername(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Signing In...' : 'Sign In'}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(true)}
                      className="toggle-form-btn"
                    >
                      Need an account? Register
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowForgotPassword(true)}
                      style={{background: 'none', border: 'none', color: '#666', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', marginTop: '12px', padding: '4px 0'}}
                    >
                      Forgot Password?
                    </button>
                  </div>
                ) : (
                  <div>
                    <form onSubmit={handleRegister} className="signin-form">
                      <h3>Register</h3>
                      <input
                        type="text"
                        placeholder="Username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        required
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        value={registerData.phoneNumber}
                        onChange={(e) => setRegisterData({...registerData, phoneNumber: e.target.value})}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Full Name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Registering...' : 'Register'}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(false)}
                      className="toggle-form-btn"
                    >
                      Have an account? Sign In
                    </button>
                  </div>
                )
              ) : (
                <div className="user-info">
                  <h3>User Info</h3>
                  <p><strong>Name:</strong> {user.username}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Role:</strong> {user.roles?.join(', ')}</p>
                  <button onClick={handleLogOut} className="logout-btn">
                    Log Off
                  </button>
                </div>
              )}
            </div>
          )}</div></FleetLandingDock>
      <div className="fullscreen-chat-container">
        {showIntroVideo && (
          <div className="intro-video-overlay hopkins-split-hero">
            {!showGlobeMap && !showMapPage && (
              <FleetLandingHeader
                onAbout={() => navigate('/agent')}
                onContact={() => setShowConnectModal(true)}
                onToggleTheme={handleToggle}
                isDarkMode={isDarkMode}
                navigationExtra={
                  <a className="hopkins-chamber-link" href={landingPageConfig.MARICOPA_APP.ROUTE} aria-label="Download the Maricopa Chamber app">
                    Chamber app <span aria-hidden="true">↗</span>
                  </a>
                }
              />
            )}
            <section className="hopkins-hero-copy" aria-labelledby="hopkins-hero-title">
              <div className="hopkins-hero-rule" aria-hidden="true"><span></span></div>
              <span className="hopkins-hero-eyebrow">Maricopa · Arizona</span>
              <h1 id="hopkins-hero-title">Real estate rooted in <em>community.</em></h1>
              <p>Local knowledge, honest guidance, and a smarter way to discover where you belong in Maricopa.</p>

              <div className="hopkins-hero-actions">
                <Link className="hopkins-setup-link" to="/347-directory/setup">
                  Add Your Business + Chamber App <span aria-hidden="true">→</span>
                </Link>
                <button type="button" onClick={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setSelectedCity(null);setShowGlobeMap(true);}}>Explore homes <span aria-hidden="true">↗</span></button>
                <button type="button" onClick={() => navigate('/agent')}>Meet Sandy</button>
                <Link className="hopkins-directory-link" to="/347-directory">
                  347 Business Directory <span aria-hidden="true">→</span>
                </Link>
                <a
                  className="hopkins-renovations-link"
                  href="https://maricoparenovations.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Maricopa Renovations <span aria-hidden="true">↗</span>
                </a>
              </div>

              <div className="hopkins-hero-contact" aria-label={`Contact ${SITE_CONFIG.AGENT.NAME}`}>
                <a href={`tel:${SITE_CONFIG.AGENT.PHONE.replace(/[^\d+]/g, '')}`}>
                  <span>Call or text Sandy</span>
                  <strong>{SITE_CONFIG.AGENT.PHONE}</strong>
                </a>
                <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>
                  <span>Email</span>
                  <strong>{SITE_CONFIG.AGENT.EMAIL}</strong>
                </a>
              </div>

              <img className="hopkins-wave-image" src={landingPageConfig.WAVE_IMAGE} alt="Illustration of Sandy Hopkins waving" />
            </section>

            <section className="hopkins-hero-media" aria-label="Maricopa community film and client reviews">
              <div className="hopkins-media-frame">
                <video
                  ref={videoRef}
                  className="intro-video hopkins-half-video"
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
                <div className="hopkins-media-shade" aria-hidden="true"></div>
                <div className="hopkins-media-caption">
                  <span>Local perspective</span>
                  <strong>Move with confidence.</strong>
                </div>
                <div className="hopkins-brokerage-mark">
                  <img src="/maricopa-real-estate-company.png" alt={`${SITE_CONFIG.AGENT.BROKERAGE_NAME} logo`} />
                </div>
              </div>
              <div ref={reviewCarouselRef} className="review-carousel-container hopkins-review-carousel">
                <div className="hopkins-review-heading">
                  <span aria-hidden="true">★★★★★</span>
                  <strong>What Sandy's clients say</strong>
                  <small>Scroll for more</small>
                </div>
                <div className="review-card">
                  <p className="review-text">"Sandy helped us sell our home in weeks. It was our first time through the process, and she explained everything."</p>
                  <p className="review-author">Adam B · Seller</p>
                </div>
                <div className="review-card">
                  <p className="review-text">"We moved across the country and are so grateful we found Sandy. She helped us navigate every criterion we were looking for."</p>
                  <p className="review-author">Greg N · Buyer</p>
                </div>
                <div className="review-card">
                  <p className="review-text">"Sandy helped us sell fast and was an expert throughout the entire process."</p>
                  <p className="review-author">Cynthia W · Seller</p>
                </div>
              </div>
            </section>

            <div className={`hopkins-concierge-dock ${showAgentSection ? 'compact' : ''}`}>
              <div className="hero-search-wrapper hopkins-hero-search">
                <label htmlFor="hopkins-area-search">Explore an area</label>
                <div>
                  <select id="hopkins-area-search" className="hero-search-select" onChange={handleAreaSearch} defaultValue="">
                    <option value="" disabled>Search by community...</option>
                    {SERVICE_AREAS.map((area, i) => (
                      <option key={`${area.label}-${i}`} value={i}>{area.label}</option>
                    ))}
                  </select>
                  <svg className="hero-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
              </div>
              <div className="landing-action-buttons hopkins-dock-actions">
                <button type="button" className="landing-action-btn contact-btn" onClick={() => setShowConnectModal(true)}>
                  <span>Contact</span><small>Start a conversation</small>
                </button>
                <Link className="landing-action-btn first-fridays-btn" to="/first-fridays">
                  <span>First Fridays</span><small>Explore the local event</small>
                </Link>
              </div>
            </div>
          </div>
        )}
        {showIntroVideo && (
          <>
            <div className="agent-intro-page">
              {showAgentSection && (
                <div className="agent-intro-card">
                  <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/raw-s-hopkins.png" alt={SITE_CONFIG.AGENT.NAME} className="agent-intro-photo" />
                  <div className="agent-intro-text">
                    <h2>Meet {SITE_CONFIG.AGENT.NAME}</h2>
                    <p>Your trusted real estate professional in {SITE_CONFIG.STATE}. Dedicated to providing exceptional service and expert guidance through every step of your home buying or selling journey.</p>
                    <button className="agent-intro-btn" onClick={() => navigate('/agent')}>Learn More</button>
                  </div>
                </div>
              )}
            </div>
            <section className="hoa-teaser-page" aria-labelledby="hoa-teaser-title">
              <div className="hoa-teaser-panel">
                <div className="hoa-teaser-copy">
                  <span className="hoa-teaser-eyebrow">Maricopa HOA Research Center</span>
                  <h2 id="hoa-teaser-title">Know the HOA before you fall in love with the home.</h2>
                  <p>Compare Maricopa communities, management contacts, historical fee estimates, and the documents every buyer should review before closing.</p>
                  <div className="hoa-teaser-actions">
                    <button className="hoa-teaser-primary" onClick={() => navigate('/maricopa-hoas')}>Explore all HOA guides</button>
                    <button className="hoa-teaser-secondary" onClick={() => navigate('/maricopa-hoas/province')}>View Province guide</button>
                  </div>
                </div>
                <div className="hoa-teaser-research">
                  <div className="hoa-teaser-metrics">
                    <div><strong>21</strong><span>Community guides</span></div>
                    <div><strong>Clear</strong><span>Fee snapshots</span></div>
                    <div><strong>Smart</strong><span>Buyer checklists</span></div>
                  </div>
                  <div className="hoa-teaser-links">
                    <span>Popular community research</span>
                    <button onClick={() => navigate('/maricopa-hoas/rancho-el-dorado')}>Rancho El Dorado <b>→</b></button>
                    <button onClick={() => navigate('/maricopa-hoas/cobblestone-farms')}>Cobblestone Farms <b>→</b></button>
                    <button onClick={() => navigate('/maricopa-hoas/tortosa')}>Tortosa <b>→</b></button>
                    <button onClick={() => navigate('/maricopa-hoas/villages-at-rancho-el-dorado')}>The Villages <b>→</b></button>
                  </div>
                </div>
              </div>
              <p className="hoa-teaser-disclaimer">HOA information is a research starting point. Current fees and rules must be verified with the association.</p>
            </section>
            <div className={`image-page ${showImagePageText ? 'visible' : ''}`}>
              {showImagePageText && (
                <>
                  <div className="image-page-text">
                    <h1>Ready for your Arizona Move?</h1>
                    <h2>With Sandy Hopkins Real Estate Group</h2>
                    <p>A unique collection of rare and exclusive properties. Customize your search for the perfect property by using our advanced search technology.</p>
                  </div>
                  <div className="featured-properties-widget">
                    <h3>Featured Properties</h3>
                    {featuredProperties.length > 0 && (
                      <>
                        <div
                          className="property-carousel"
                          onTouchStart={(e) => { propertyTouchStartX.current = e.touches[0].clientX; }}
                          onTouchEnd={(e) => {
                            if (propertyTouchStartX.current === null) return;
                            const delta = e.changedTouches[0].clientX - propertyTouchStartX.current;
                            if (Math.abs(delta) > 40) {
                              setCurrentPropertyIndex((prev) =>
                                delta < 0
                                  ? (prev + 1) % featuredProperties.length
                                  : (prev - 1 + featuredProperties.length) % featuredProperties.length
                              );
                            }
                            propertyTouchStartX.current = null;
                          }}
                        >
                          <div className="property-carousel-track">
                            {featuredProperties.map((property, index) => {
                              const offset = index - currentPropertyIndex;
                              const isActive = offset === 0;
                              return (
                                <div
                                  key={index}
                                  className={`featured-property-card ${isActive ? 'active' : ''}`}
                                  style={{
                                    transform: `translateX(${offset * 78}%) scale(${isActive ? 1 : 0.82})`,
                                    opacity: Math.abs(offset) > 2 ? 0 : isActive ? 1 : 0.55,
                                    pointerEvents: isActive ? 'auto' : 'none',
                                    zIndex: 10 - Math.abs(offset)
                                  }}
                                  onClick={() => {
                                    if (isActive) {
                                      window.history.pushState(null, '', `/listing/${property.listing_key}`);
                                      openPropertyFromSummary(property);
                                    } else {
                                      setCurrentPropertyIndex(index);
                                    }
                                  }}
                                >
                                  <img src={property.first_photo_url} alt={formatStreetAddress(property)} />
                                  <div className="property-info">
                                    <p className="property-price">${property.list_price?.toLocaleString() || property.current_price?.toLocaleString()}</p>
                                    <p className="property-address">{formatPropertyAddress(property)}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="property-nav-buttons">
                          <button aria-label="Previous property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length); }}>←</button>
                          <div className="property-dots">
                            {featuredProperties.map((_, index) => (
                              <button
                                key={index}
                                aria-label={`Go to property ${index + 1}`}
                                className={`property-dot ${index === currentPropertyIndex ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex(index); }}
                              />
                            ))}
                          </div>
                          <button aria-label="Next property" onClick={(e) => { e.stopPropagation(); setCurrentPropertyIndex((prev) => (prev + 1) % featuredProperties.length); }}>→</button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="e-image-page">
              {showEPage && (
                <div className="home-value-section">
                  <div className="home-value-image-col">
                    <img src="https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-ok-001.png" alt="Arizona Home" className="home-value-img" />
                  </div>
                  <div className="home-value-content-col">
                    <span className="home-value-eyebrow">Free for Homeowners</span>
                    <h2 className="e-image-heading">What's Your Home Worth?</h2>
                    <p className="home-value-subtext">Get a personalized home value estimate — no obligation, no pressure. Just honest insight from a local expert who knows the Maricopa market.</p>
                    <button className="home-value-btn" onClick={() => setShowHomeValueModal(true)}>Get My Free Estimate</button>
                  </div>
                </div>
              )}
            </div>
            <div className={`city-tiles-page ${showCityTiles ? 'visible' : ''}`}>
                <h2 className="city-tiles-title">Areas We Serve</h2>
                <div className="city-tiles-grid">
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-001.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/maricopa')}>Maricopa</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-002.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/ahwatukee')}>Ahwatukee</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-003.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/chandler')}>Chandler</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-004.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/queen-creek')}>Queen Creek</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-005.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/gilbert')}>Gilbert</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenery-006.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/tempe')}>Tempe</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic--az-southmountain-001.jpg)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/mesa')}>Mesa</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-southmountain-002.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/casa-grande')}>Casa Grande</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-south-street.jpeg)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/goodyear')}>Goodyear</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-southmountain-home-001.jpg)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/buckeye')}>Buckeye</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-001.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/san-tan-valley')}>San Tan Valley</div>
                  <div className="city-tile" style={{backgroundImage: 'url(https://agentwinds-client-landings.s3.us-east-1.amazonaws.com/generic-az-scenerey-002.png)', backgroundPosition: 'center center', backgroundSize: 'cover'}} onClick={() => navigate('/area/coolidge')}>Coolidge</div>
                </div>
                <nav className="maricopa-resource-hub" aria-labelledby="maricopa-resource-title">
                  <div className="maricopa-resource-heading">
                    <span>Local knowledge, thoughtfully organized</span>
                    <h2 id="maricopa-resource-title">Explore Maricopa with Sandy</h2>
                    <p>Clear, practical guides for buying, selling, relocating, and choosing the right Maricopa community.</p>
                  </div>
                  <div className="maricopa-resource-groups">
                    {MARICOPA_RESOURCE_GROUPS.map((group) => (
                      <section className="maricopa-resource-group" key={group.title}>
                        <h3>{group.title}</h3>
                        {group.links.map(([label, path]) => (
                          <Link key={path} to={path}>{label}<span aria-hidden="true">↗</span></Link>
                        ))}
                      </section>
                    ))}
                  </div>
                </nav>
          </div>
          </>
        )}
        <div className={`chat-wrapper ${!showIntroVideo ? 'fullscreen' : 'hidden'}`}>
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
            hidePrompts={!showIntroVideo}
          />
        </div>
      </div>

      {showMapPage && (
        <div className="full-map-page">
          <button 
            className="close-map-page"
            onClick={() => setShowMapPage(false)}
          >
            ← Back to Chat
          </button>
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isMapPage={true}
            onMapPageClose={() => setShowMapPage(false)}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showGlobeMap && (
        <div className="full-map-page">
          <ActionButtons key={location.key}
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isGlobeMap={true}
            onGlobeMapResultsChange={setShowGlobeMapResults}
            onGlobeMapClose={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/');setSelectedCity(null);
              setShowGlobeMap(false);
              setShowGlobeMapResults(false);
              setSelectedCity(null);
              setShowIntroVideo(true);
            }}
            initialCity={selectedCity}
            directListingKey={directListing ? listingKey : null}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showBlogs && (
        <BlogsPage onClose={() => setShowBlogs(false)} />
      )}
      {showReviews && (
        <ReviewsPage onClose={() => setShowReviews(false)} />
      )}

      <footer className={`landing-footer ${!showIntroVideo ? 'hidden' : ''}`}>
        <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
        <span className="footer-separator"> | </span>
        <a href="/terms-of-service" className="footer-link">Terms of Service</a>
        <span className="footer-separator"> | </span>
        <button 
          onClick={() => setShowMLSDisclosure(true)}
          className="footer-link mls-disclosure-link"
        >
          MLS Disclosure
        </button>
        <span className="footer-separator-logo"> | </span>
        <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer">
          <img src="https://aw-business-marketing.s3.us-east-1.amazonaws.com/websitesbyaw-transparent.png" alt="Websites by AW" className="footer-logo" />
        </a>
      </footer>

      {showMLSDisclosure && (
        <div className="modal-overlay" onClick={() => setShowMLSDisclosure(false)}>
          <div className="mls-disclosure-modal" onClick={(e) => e.stopPropagation()}>
            <h3>MLS Disclosure</h3>
            <p>
              but not guaranteed. All properties are subject to prior sale, change or withdrawal. 
              Neither listing broker(s) or information provider(s) shall be responsible for any typographical errors, 
              misinformation, misprints and shall be held totally harmless. Listing(s) information is provided for 
              consumer's personal, non-commercial use and may not be used for any purpose other than to identify 
              prospective properties consumers may be interested in purchasing. The data relating to real estate for 
              sale on this website comes in part from the Internet Data Exchange program of the Multiple Listing Service. 
              Real estate listings held by brokerage firms other than Providence Realty may be marked with the Internet 
              Data Exchange logo and detailed information about those properties will include the name of the listing 
              broker(s) when required by the MLS. Copyright ©2025 All rights reserved. Last Updated: December 20, 2025 7:58 PM UTC
            </p>
            <button onClick={() => setShowMLSDisclosure(false)} className="close-disclosure-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {showConnectSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Thank you! We'll be in touch soon.</span>
          </div>
        </div>
      )}

      {showHomeValueSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Got it! Sandy will personally reach out to provide your free home evaluation.</span>
          </div>
        </div>
      )}

      {showHomeValueModal && (
        <div className="modal-overlay" onClick={() => setShowHomeValueModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Free Home Value Estimate</h3>
            <form onSubmit={handleHomeValueSubmit}>
          <HoneypotField />
              <input type="text" placeholder="Full Name" value={homeValueForm.name} onChange={(e) => setHomeValueForm({...homeValueForm, name: e.target.value})} required />
              <input type="email" placeholder="Email" value={homeValueForm.email} onChange={(e) => setHomeValueForm({...homeValueForm, email: e.target.value})} required />
              <input type="tel" placeholder="Phone Number" value={homeValueForm.phoneNumber} onChange={(e) => setHomeValueForm({...homeValueForm, phoneNumber: e.target.value})} required />
              <input type="text" placeholder="ZIP Code" value={homeValueForm.postalCode} onChange={(e) => setHomeValueForm({...homeValueForm, postalCode: e.target.value})} required />
              <label className="sms-consent-control">
                <input type="checkbox" checked={homeValueForm.smsConsent} onChange={(e) => setHomeValueForm({...homeValueForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <button type="submit" disabled={submittingHomeValue}>{submittingHomeValue ? 'Submitting...' : 'Submit'}</button>
              <button type="button" onClick={() => setShowHomeValueModal(false)} className="cancel-btn">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="connect-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ready to Connect?</h3>
            <form onSubmit={handleConnectSubmit}>
          <HoneypotField />
              <input
                type="text"
                placeholder="Full Name"
                value={connectForm.name}
                onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={connectForm.email}
                onChange={(e) => setConnectForm({...connectForm, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={connectForm.phoneNumber}
                onChange={(e) => setConnectForm({...connectForm, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={connectForm.postalCode}
                onChange={(e) => setConnectForm({...connectForm, postalCode: e.target.value})}
                required
              />
              <label className="sms-consent-control">
                <input type="checkbox" checked={connectForm.smsConsent} onChange={(e) => setConnectForm({...connectForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <select
                value={connectForm.serviceReq}
                onChange={(e) => setConnectForm({...connectForm, serviceReq: e.target.value})}
                required
              >
                <option value="buying">Buying</option>
                <option value="selling">Selling</option>
                <option value="both">Both</option>
              </select>
              <button type="submit" disabled={submittingConnect}>
                {submittingConnect ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={() => setShowConnectModal(false)} className="cancel-btn">
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      <PropertyDetailModal
        propertyDetail={propertyDetail}
        propertyDetailImages={propertyDetailImages}
        imagesLoading={imagesLoading}
        onClose={() => {
          closePropertyDetail();
          window.history.pushState(null, '', '/');
        }}
        user={user}
        isAdmin={isAdmin}
        likedProperties={likedProperties}
        setLikedProperties={setLikedProperties}
      />

    </div>
  );
};

export default LandingPage;
